import React, { useState, useEffect, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { 
  Box, Typography, Paper, Stack, Button, useTheme, TextField, alpha, 
  IconButton, useMediaQuery, Chip, List, ListItem, ListItemText, ListItemIcon
} from "@mui/material";
import { 
  CheckCircleOutlined, CancelOutlined, WarningOutlined, QrCodeScannerOutlined, 
  FlashOn as FlashOnIcon, History as HistoryIcon, Keyboard, InfoOutlined, Close, 
  Add, Remove, PhotoCamera, Inventory2, EditNote, CenterFocusWeak,
  Settings as SettingsIcon, FlashOff as FlashOffIcon, FiberManualRecord, ConfirmationNumberOutlined
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PartnerScanner = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [isFlashOn, setIsFlashOn] = useState(false);
  const { user: clerkUser } = useUser();
  const [manualToken, setManualToken] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0: Scan, 1: History, 2: Manual, 3: Settings
  const [scanHistory, setScanHistory] = useState([]);
  const scannerRef = useRef(null);

  // Neomorphic design tokens
  const neoRaised = "6px 6px 12px rgba(0,0,0,0.08), -6px -6px 12px rgba(255,255,255,0.6)";
  const neoInset = "inset 6px 6px 12px rgba(0,0,0,0.08), inset -6px -6px 12px rgba(255,255,255,0.6)";
  const neoRaisedSmall = "4px 4px 8px rgba(0,0,0,0.06), -4px -4px 8px rgba(255,255,255,0.5)";
  const neoInsetSmall = "inset 4px 4px 8px rgba(0,0,0,0.06), inset -4px -4px 8px rgba(255,255,255,0.5)";

  useEffect(() => {
    // Stop camera when leaving scan tab
    if (activeTab !== 0 && cameraActive) {
      stopScanner();
    }
  }, [activeTab]);

  useEffect(() => {
    return () => {
      if (scannerRef.current) stopScanner();
    };
  }, []);

  const playBeep = (type = 'success') => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(type === 'success' ? 800 : 300, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, audioCtx.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.2);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.2);
    } catch (e) { }
  };

  const startScanner = async () => {
    if (cameraActive) return;
    setResult(null);
    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode("reader");
      }
      const config = { 
        fps: 20, 
        qrbox: (w, h) => {
          const minDim = Math.min(w, h);
          return { width: minDim * 0.7, height: minDim * 0.7 };
        },
        aspectRatio: 1.0,
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
      };
      await scannerRef.current.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          if (navigator.vibrate) navigator.vibrate(100);
          playBeep('success');
          handleVerify(decodedText);
        },
        () => {}
      );
      setCameraActive(true);
    } catch (err) {
      console.error(err);
      toast.error("Camera access denied or device busy");
      setCameraActive(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      }
    } catch (e) { }
    setCameraActive(false);
    setIsFlashOn(false);
  };

  const toggleFlash = async () => {
    if (!cameraActive || !scannerRef.current) return;
    try {
      const targetState = !isFlashOn;
      await scannerRef.current.applyVideoConstraints({
        advanced: [{ torch: targetState }]
      });
      setIsFlashOn(targetState);
    } catch (err) {
      toast.warning("Flash is not supported on this camera");
    }
  };

  const handleVerify = async (qrData) => {
    if (!qrData) return;
    setScanning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API_BASE}/tickets/verify`, { 
        qrToken: qrData,
        scannerUserId: clerkUser?.id || 'unknown'
      });
      
      const resData = res.data;
      setResult(resData);
      
      // Update session history
      setScanHistory(prev => [{
        id: Date.now(),
        token: qrData,
        time: new Date().toLocaleTimeString(),
        status: resData.code || 'UNKNOWN',
        movie: resData.movieTitle || 'Movie Ticket',
        valid: resData.valid
      }, ...prev].slice(0, 30));

      if (!resData.valid) playBeep('error');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Verification failed';
      setResult({ code: 'ERROR', message: errorMsg, valid: false });
      playBeep('error');
    }
    setScanning(false);
    if (activeTab === 0) stopScanner(); // Show result instead of active camera
  };

  const resultConfig = {
    VALID: { icon: <CheckCircleOutlined />, color: '#10b981', label: 'ACCESS GRANTED' },
    ALREADY_USED: { icon: <WarningOutlined />, color: '#f59e0b', label: 'DUPLICATE ENTRY' },
    EXPIRED: { icon: <CancelOutlined />, color: '#ef4444', label: 'TICKET EXPIRED' },
    ERROR: { icon: <CancelOutlined />, color: '#ef4444', label: 'SYSTEM ERROR' },
  };

  const rc = result ? (resultConfig[result.code] || resultConfig.ERROR) : null;

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#e8eaf0', 
      pb: isDesktop ? 6 : 14,
      position: 'relative',
      fontFamily: "'Plus Jakarta Sans', sans-serif"
    }}>
      
      {/* TopAppBar */}
      <Box component="nav" sx={{ 
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100,
        height: 64, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#e8eaf0', borderRadius: '0 0 24px 24px',
        boxShadow: neoRaised
      }}>
        <IconButton sx={{ color: '#6366f1', ...neoRaisedSmall }} onClick={() => setActiveTab(0)}>
          <QrCodeScannerOutlined />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2e3040', letterSpacing: '-0.5px' }}>SilkScan</Typography>
        <IconButton 
          onClick={toggleFlash}
          sx={{ color: isFlashOn ? '#f59e0b' : '#6366f1', ...neoRaisedSmall, transition: '0.3s' }}
        >
          {isFlashOn ? <FlashOnIcon /> : <FlashOffIcon />}
        </IconButton>
      </Box>

      {/* Main Container */}
      <Box component="main" sx={{ pt: 12, px: 2, maxWidth: 500, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        
        <AnimatePresence mode="wait">
          {activeTab === 0 && (
            <motion.div key="scan" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}>
              <Box sx={{ 
                width: '100%', aspectRatio: '1/1', bgcolor: '#e8eaf0', borderRadius: '32px',
                p: 0, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
                boxShadow: neoInset, mb: 4
              }}>
                <Box sx={{ 
                  flex: 1, borderRadius: '32px', bgcolor: '#000', position: 'relative', overflow: 'hidden',
                }}>
                  <Box id="reader" sx={{ 
                    width: '100%', height: '100%', 
                    '& video': { 
                      width: '100% !important', 
                      height: '100% !important', 
                      objectFit: 'cover !important',
                      borderRadius: '32px'
                    },
                    '& #reader__scan_region': {
                        width: '100% !important',
                        height: '100% !important',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& canvas': {
                            width: '100% !important',
                            height: '100% !important',
                            objectFit: 'cover !important',
                            borderRadius: '32px'
                        }
                    }
                  }} />
                  
                  {!cameraActive && !result && !scanning && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Stack spacing={2} alignItems="center">
                        <PhotoCamera sx={{ fontSize: 60, color: alpha('#fff', 0.1) }} />
                        <Typography sx={{ color: alpha('#fff', 0.4), fontWeight: 600 }}>Sensor Offline</Typography>
                        <Button variant="contained" onClick={startScanner} sx={{ borderRadius: '50px', bgcolor: '#6366f1' }}>Sync Camera</Button>
                      </Stack>
                    </Box>
                  )}

                  {/* Overlays */}
                  {cameraActive && !result && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <Box sx={{ width: '70%', height: '70%', position: 'relative' }}>
                        <Box sx={{ position: 'absolute', top: -4, left: -4, width: 32, height: 32, borderTop: '4px solid #6366f1', borderLeft: '4px solid #6366f1', borderRadius: '16px 0 0 0' }} />
                        <Box sx={{ position: 'absolute', top: -4, right: -4, width: 32, height: 32, borderTop: '4px solid #6366f1', borderRight: '4px solid #6366f1', borderRadius: '0 16px 0 0' }} />
                        <Box sx={{ position: 'absolute', bottom: -4, left: -4, width: 32, height: 32, borderBottom: '4px solid #6366f1', borderLeft: '4px solid #6366f1', borderRadius: '0 0 0 16px' }} />
                        <Box sx={{ position: 'absolute', bottom: -4, right: -4, width: 32, height: 32, borderBottom: '4px solid #6366f1', borderRight: '4px solid #6366f1', borderRadius: '0 0 16px 0' }} />
                        <Box sx={{ 
                          position: 'absolute', top: '50%', left: 16, right: 16, height: '2px', bgcolor: '#6366f1', 
                          boxShadow: "0 0 15px rgba(99,102,241,1)",
                          animation: 'scanLine 2s linear infinite'
                        }} />
                      </Box>
                      <Box sx={{ position: 'absolute', bottom: 20, px: 2, py: 0.5, bgcolor: alpha('#fff', 0.15), borderRadius: '8px', backdropFilter: 'blur(4px)' }}>
                        <Typography sx={{ color: '#fff', fontSize: '10px', fontWeight: 900, letterSpacing: '0.5px' }}>SCAN AUTHENTICATION TOKEN</Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Verifying Spinner */}
                  {scanning && (
                    <Box sx={{ 
                      position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10
                    }}>
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}>
                        <Box sx={{ width: 48, height: 48, border: '4px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%' }} />
                      </motion.div>
                      <Typography sx={{ color: '#fff', fontWeight: 900, mt: 3, letterSpacing: '3px', fontSize: '12px' }}>VALIDATING...</Typography>
                    </Box>
                  )}

                  {/* Enhanced Result Card */}
                  {result && (
                    <Box sx={{ 
                      position: 'absolute', inset: 0, bgcolor: alpha(rc.color, 0.98), 
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                      p: 4, textAlign: 'center', zIndex: 20 
                    }}>
                      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                        <Box sx={{ color: '#fff', mb: 2 }}>{React.cloneElement(rc.icon, { sx: { fontSize: 90 } })}</Box>
                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.75rem', mb: 1, letterSpacing: '-1px' }}>{rc.label}</Typography>
                        <Typography sx={{ color: '#fff', mb: 4, fontWeight: 500, fontSize: '1.1rem', opacity: 0.95 }}>{result.message}</Typography>
                        <Button 
                          variant="contained" 
                          sx={{ bgcolor: '#fff', color: rc.color, borderRadius: '50px', px: 6, py: 1.8, fontWeight: 900, fontSize: '1rem', '&:hover': { bgcolor: '#f5f5f5' } }} 
                          onClick={() => { setResult(null); startScanner(); }}
                        >
                          READY FOR NEXT
                        </Button>
                      </motion.div>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Shutter Cluster */}
              <Stack direction="row" spacing={3} alignItems="center" justifyContent="center">
                <IconButton sx={{ width: 64, height: 64, ...neoRaised, '&:active': { boxShadow: neoInsetSmall } }}>
                  <Remove />
                </IconButton>
                <IconButton 
                  onClick={cameraActive ? () => toast.info("Scanner is listening") : startScanner}
                  sx={{ width: 100, height: 100, ...neoInset, color: '#6366f1', p: 1.5 }}
                >
                  <Box sx={{ 
                    width: '100%', height: '100%', borderRadius: '50%', bgcolor: '#e8eaf0', 
                    boxShadow: neoRaised, display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {cameraActive ? <FiberManualRecord sx={{ color: '#ef4444', animation: 'blink 1.5s infinite' }} /> : <PhotoCamera sx={{ fontSize: 36 }} />}
                  </Box>
                </IconButton>
                <IconButton sx={{ width: 64, height: 64, ...neoRaised, '&:active': { boxShadow: neoInsetSmall } }}>
                  <Add />
                </IconButton>
              </Stack>
            </motion.div>
          )}

          {activeTab === 1 && (
            <motion.div key="history" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <Paper sx={{ p: 4, borderRadius: '32px', bgcolor: '#e8eaf0', boxShadow: neoRaised }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <HistoryIcon color="primary" /> Session Logs
                </Typography>
                {scanHistory.length === 0 ? (
                  <Box sx={{ py: 8, textAlign: 'center', opacity: 0.4 }}>
                    <Inventory2 sx={{ fontSize: 60, mb: 1 }} />
                    <Typography fontWeight={600}>No scan activity recorded</Typography>
                  </Box>
                ) : (
                  <List sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {scanHistory.map((scan) => (
                      <ListItem key={scan.id} sx={{ bgcolor: '#e8eaf0', borderRadius: '20px', boxShadow: neoRaisedSmall, p: 2 }}>
                        <ListItemIcon sx={{ minWidth: 48 }}>
                          {scan.valid ? <CheckCircleOutlined sx={{ color: '#10b981', fontSize: 32 }} /> : <CancelOutlined sx={{ color: '#ef4444', fontSize: 32 }} />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={scan.movie} 
                          secondary={`${scan.time} • ${scan.token.slice(0, 10)}`} 
                          primaryTypographyProps={{ fontWeight: 800, fontSize: '0.95rem', color: '#2e3040' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 600 }}
                        />
                        <Chip 
                          label={scan.status.replace('_', ' ')} 
                          size="small" 
                          sx={{ fontWeight: 900, fontSize: '9px', bgcolor: scan.valid ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: scan.valid ? '#10b981' : '#ef4444', border: 'none' }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {scanHistory.length > 0 && (
                  <Button fullWidth onClick={() => setScanHistory([])} sx={{ mt: 4, fontWeight: 700, opacity: 0.5 }}>Clear Logs</Button>
                )}
              </Paper>
            </motion.div>
          )}

          {activeTab === 2 && (
            <motion.div key="manual" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}>
              <Paper sx={{ p: 4, borderRadius: '32px', bgcolor: '#e8eaf0', boxShadow: neoRaised }}>
                <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 4 }}>
                  <Box sx={{ p: 1.8, borderRadius: '16px', bgcolor: '#6366f1', color: '#fff', boxShadow: "0 8px 16px rgba(99,102,241,0.3)" }}>
                    <ConfirmationNumberOutlined sx={{ fontSize: 30 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>Manual Entry</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#6c6e7e' }}>ENTER ACCESS TOKEN MANUALLY</Typography>
                  </Box>
                </Stack>
                
                <TextField 
                  fullWidth autoFocus variant="filled" label="Token ID"
                  value={manualToken} onChange={(e) => setManualToken(e.target.value.toUpperCase())}
                  sx={{ '& .MuiFilledInput-root': { bgcolor: '#e8eaf0', borderRadius: '20px', boxShadow: neoInsetSmall, border: 'none', '&:before, &:after': { display: 'none' } } }}
                  placeholder="EX: TKT-P102-X9"
                />
                
                <Stack direction="row" spacing={1.5} sx={{ mt: 3, mb: 5 }}>
                  {['BK-', 'TKT-', 'USR-'].map(prefix => (
                    <Chip key={prefix} label={prefix} onClick={() => setManualToken(prefix)} sx={{ ...neoRaisedSmall, bgcolor: '#e8eaf0', fontWeight: 800, px: 1 }} />
                  ))}
                </Stack>

                <Button 
                  fullWidth variant="contained" size="large"
                  onClick={() => handleVerify(manualToken)}
                  disabled={!manualToken || scanning}
                  sx={{ borderRadius: '50px', bgcolor: '#6366f1', py: 2.2, fontWeight: 900, fontSize: '1.1rem', boxShadow: "0 10px 20px rgba(99,102,241,0.4)", textTransform: 'none' }}
                >
                  Verify Ticket
                </Button>
              </Paper>
            </motion.div>
          )}

          {activeTab === 3 && (
            <motion.div key="settings" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Paper sx={{ p: 4, borderRadius: '32px', bgcolor: '#e8eaf0', boxShadow: neoRaised }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 4 }}>Scanner Precision</Typography>
                <Stack spacing={3}>
                  <Box sx={{ p: 2.5, borderRadius: '20px', boxShadow: neoRaisedSmall, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight={700}>Continuous Focus</Typography>
                    <Chip label="ACTIVE" color="primary" size="small" sx={{ fontWeight: 900 }} />
                  </Box>
                  <Box sx={{ p: 2.5, borderRadius: '20px', boxShadow: neoRaisedSmall, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight={700}>Haptic Response</Typography>
                    <Chip label="ENABLED" color="primary" size="small" sx={{ fontWeight: 900 }} />
                  </Box>
                  <Box sx={{ p: 2.5, borderRadius: '20px', boxShadow: neoRaisedSmall, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight={700}>Audio Telemetry</Typography>
                    <Chip label="STANDBY" color="primary" size="small" sx={{ fontWeight: 900 }} />
                  </Box>
                </Stack>
                <Button fullWidth variant="outlined" sx={{ mt: 5, borderRadius: '50px', borderWidth: 2, fontWeight: 800 }}>FACTORY RESET</Button>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Console Status */}
        {activeTab === 0 && (
          <Box sx={{ 
            bgcolor: '#e8eaf0', borderRadius: '28px', p: 3, display: 'flex', alignItems: 'center', gap: 3,
            boxShadow: neoRaised
          }}>
            <Box sx={{ 
              width: 58, height: 58, borderRadius: '20px', bgcolor: '#e8eaf0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1',
              boxShadow: neoInsetSmall
            }}>
              <Inventory2 sx={{ fontSize: 30 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '11px', color: '#6c6e7e', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', mb: 0.5 }}>
                Subsystem Link
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#2e3040', fontWeight: 900 }}>
                {cameraActive ? 'Sensor: Synchronized' : 'Sensor: Standby'}
              </Typography>
            </Box>
            <IconButton onClick={() => setActiveTab(2)} sx={{ bgcolor: '#e8eaf0', ...neoRaisedSmall, color: '#6366f1', p: 1.5 }}>
              <Keyboard />
            </IconButton>
          </Box>
        )}

      </Box>

      {/* Control Navigation (Mobile Only) */}
      <Box component="nav" sx={{ 
        display: { xs: 'flex', md: 'none' },
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100,
        height: 100, px: 3, pb: 3, pt: 1, justifyContent: 'space-around', alignItems: 'center',
        bgcolor: '#e8eaf0', borderRadius: '36px 36px 0 0',
        boxShadow: "0px -15px 40px rgba(0,0,0,0.12)"
      }}>
        {[
          { icon: <CenterFocusWeak />, label: 'Scan', id: 0 },
          { icon: <HistoryIcon />, label: 'Audit', id: 1 },
          { icon: <EditNote />, label: 'Manual', id: 2 },
          { icon: <SettingsIcon />, label: 'Config', id: 3 }
        ].map((item) => (
          <Box key={item.id} sx={{ 
            display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 1.5, borderRadius: '22px',
            color: activeTab === item.id ? '#6366f1' : '#8a8c9a',
            bgcolor: activeTab === item.id ? '#e8eaf0' : 'transparent',
            boxShadow: activeTab === item.id ? neoInsetSmall : 'none',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            cursor: 'pointer'
          }} onClick={() => setActiveTab(item.id)}>
            {React.cloneElement(item.icon, { sx: { fontSize: 30 } })}
            <Typography sx={{ fontSize: '11px', fontWeight: activeTab === item.id ? 900 : 600, mt: 0.7 }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>

      <style>{`
        @keyframes scanLine {
          0% { top: 10%; opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { top: 90%; opacity: 0; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        body { background-color: #e8eaf0 !important; }
      `}</style>
      
    </Box>
  );
};

export default PartnerScanner;
